import {
  CheckCircledIcon,
  ExclamationTriangleIcon,
} from '@radix-ui/react-icons';
import { Badge, Flex, Text } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTableHoC } from 'components/common/table';
import { parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ShotRejectionHelper } from 'lib_ts/classes/shot-rejection.helper';
import { TrainingHelper } from 'lib_ts/classes/training.helper';
import { LOCAL_TIMEZONE } from 'lib_ts/enums/datetime.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { getConfidenceCriteria } from 'lib_ts/enums/shot-rejection.enums';
import { IDataCollection } from 'lib_ts/interfaces/i-data-collection';
import {
  ICollectionShotGroup,
  IMachineShot,
} from 'lib_ts/interfaces/training/i-machine-shot';
import { orderBy } from 'lodash';
import { useMemo } from 'react';

export interface IFiringLog {
  collection?: IDataCollection;
  shotGroups: ICollectionShotGroup[];
}

interface IProps {
  id: string;
  log: IFiringLog;
}

enum FiringLogTrackingResult {
  Captured = 'Captured',
  LowConfidence = 'LowConfidence',
}

interface IFiringLogRow extends IMachineShot {
  pitchName: string;
  numPitch: number;
  numShot: number;
  trackingResult: FiringLogTrackingResult;
}

export const FiringLog = (props: IProps) => {
  const { collection, shotGroups } = props.log;
  const numPitches = collection?.pitches?.length;

  const reqShots = useMemo(() => {
    if (!collection) {
      return {
        aiming: 0,
        analysis: 0,
      };
    }

    return TrainingHelper.getDataCollectorShots(collection?.type);
  }, [collection?.type]);

  const columns = useMemo<ITypedTableColumn<IFiringLogRow>[]>(
    () => [
      {
        label: 'Pitch Name',
        key: 'pitchName',
        classNameFn: () => 'max-width-unset',
        formatFn: (m) => (
          <Flex gap="1" direction="column" py="2">
            <Text>{m.pitchName}</Text>
            <Text size="1" color={RADIX.COLOR.SECONDARY}>
              {format(parseISO(m._created), 'MM/dd/yyyy HH:mm:ss', {
                timeZone: LOCAL_TIMEZONE,
              })}
            </Text>
          </Flex>
        ),
      },
      {
        label: 'Pitch · Shot',
        key: 'pitchShot',
        formatFn: (m) => (
          <Flex>
            <Text>{`${m.numPitch}/${numPitches} · ${m.numShot}/${
              m.aiming ? reqShots.aiming : reqShots.analysis
            }`}</Text>
          </Flex>
        ),
      },
      {
        label: 'Tracking Result',
        key: 'trackingResult',
        classNameFn: () => 'max-width-unset',
        formatFn: (m) => (
          <Flex>
            {m.trackingResult === FiringLogTrackingResult.Captured ? (
              <Badge color={RADIX.COLOR.SUCCESS}>
                <CheckCircledIcon /> Captured
              </Badge>
            ) : (
              <Badge color={RADIX.COLOR.WARNING}>
                <ExclamationTriangleIcon /> Low Confidence
              </Badge>
            )}
          </Flex>
        ),
      },
    ],
    [reqShots.aiming, reqShots.analysis, numPitches]
  );

  /**
   * Grabs all shots from each ICollectionShotGroup and adds
   * - Pitch name
   * - Low vs high confidence
   * - Which pitch # it was (e.g. 10/92)
   * - Which shot # it was (e.g. 2/3)
   */
  const firingLogRows: IFiringLogRow[] = useMemo(() => {
    const unsortedRows = shotGroups.flatMap(({ shots, pitch }) => {
      const criteria = getConfidenceCriteria(pitch?.priority);
      const sortedShots = orderBy(shots, ['_created'], ['asc']);

      let numAimingShots = 0;
      let numAnalysisShots = 0;

      const rows = sortedShots.map((shot) => {
        const pitchName = pitch?.name || 'Unknown Pitch';

        const { reject } = ShotRejectionHelper.filterShot({
          shot,
          criteria: criteria,
        });

        const trackingResult = reject
          ? FiringLogTrackingResult.LowConfidence
          : FiringLogTrackingResult.Captured;

        const pitchIndex =
          collection?.pitches?.findIndex((p) => p._id === pitch?._id) || 0;

        const numPitch = pitchIndex + 1;

        if (shot.aiming) {
          numAimingShots = numAimingShots + 1;
        } else {
          numAnalysisShots = numAnalysisShots + 1;
        }

        const numShot = shot.aiming ? numAimingShots : numAnalysisShots;

        return {
          ...shot,
          // edge case handling - we may end up with the same shots being returned for multiple pitches if the pitches have the same matching hash
          _id: `${shot._id}-${pitch?._id}`,
          pitchName,
          trackingResult,
          numPitch,
          numShot,
        };
      });

      return rows;
    });

    return orderBy(
      unsortedRows,
      ['_created', 'numPitch', 'numShot'],
      ['desc', 'desc', 'desc']
    );
  }, [collection, shotGroups]);

  return (
    <ErrorBoundary identifier="FiringLog">
      <CommonTableHoC
        dataTestID={`FiringLog-${props.id}`}
        identifier="FiringLogTable"
        displayColumns={columns}
        displayData={firingLogRows}
        scrollHeight="400px"
      />
    </ErrorBoundary>
  );
};
